import { lonLatToLocal } from './view-sync';
import { sampleRamp } from './classify';
import { verticalFrame } from './dataset-points3d';
import { RAW_FORMAT_EXTS, datasetFileExt } from './dataset-kind';

/**
 * Pure helpers for the 3D apparent-resistivity scatter of a raw survey: one point per reading from
 * GET /api/datasets/{id}/raw/pseudosection (georeferenced: lon/lat + pseudo-depth). A 3D grid
 * (`is_3d`) carries the centroid of the four electrodes, a 2D line the along-line midpoint, so both
 * are drawn the same way. No React, no three.js.
 */

const RAMP_STEPS = 64;
let ramp = null;
function rampRgb() {
  if (!ramp) {
    ramp = sampleRamp('viridis', RAMP_STEPS).map((hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255));
  }
  return ramp;
}

/** A raw resistivity survey (stg/stn/ohm/res2dinv) with a saved, non-local georeference. */
export function isScatterDataset(ds) {
  if (!ds) return false;
  const fmt = String(ds.metadata?.format || '').toLowerCase();
  const raw = RAW_FORMAT_EXTS.includes(fmt) || RAW_FORMAT_EXTS.includes(datasetFileExt(ds));
  const g = ds.metadata?.georef;
  return raw && !!g && String(g.crs || 'local').toLowerCase() !== 'local';
}

/**
 * Compact arrays from the pseudosection FeatureCollection (lon/lat, pseudo-depth down, rhoa).
 * Features without finite lon/lat/depth or a positive rhoa are dropped (the colour scale is
 * logarithmic).
 */
export function extractScatter(fc) {
  const feats = fc?.features || [];
  const n = feats.length;
  const lon = new Float64Array(n), lat = new Float64Array(n), depth = new Float32Array(n), rho = new Float32Array(n);
  const surface = new Float64Array(n); // ground elevation under each reading (NaN when the survey has none)
  let count = 0;
  for (let i = 0; i < n; i++) {
    const f = feats[i];
    const c = f?.geometry?.coordinates;
    const p = f?.properties || {};
    const d = Number.isFinite(p.pseudo_depth) ? p.pseudo_depth : (c && Number.isFinite(c[2]) ? -c[2] : NaN);
    const r = Number(p.apparent_resistivity);
    if (!c || !Number.isFinite(c[0]) || !Number.isFinite(c[1]) || !Number.isFinite(d) || !Number.isFinite(r) || r <= 0) continue;
    lon[count] = c[0]; lat[count] = c[1]; depth[count] = d; rho[count] = r;
    surface[count] = Number.isFinite(p.surface_z) ? p.surface_z : NaN;
    count++;
  }
  return {
    lon: lon.subarray(0, count), lat: lat.subarray(0, count), depth: depth.subarray(0, count), rho: rho.subarray(0, count), surface: surface.subarray(0, count),
    count, is3d: !!fc?.is_3d,
  };
}

/** Centroid origin { lon0, lat0 } of an extract, or null when empty. */
export function scatterOrigin(ext) {
  if (!ext?.count) return null;
  let sx = 0, sy = 0;
  for (let i = 0; i < ext.count; i++) { sx += ext.lon[i]; sy += ext.lat[i]; }
  return { lon0: sx / ext.count, lat0: sy / ext.count };
}

/**
/** Lowest finite `surface` of an extract (or of an array of elevations), null when there is none. */
export function lowestSurface(surface) {
  let lo = Infinity;
  for (let i = 0; i < (surface?.length || 0); i++) if (surface[i] < lo) lo = surface[i];
  return Number.isFinite(lo) ? lo : null;
}

/**
 * Position (x east, y up, z north in the shared local frame, depth below the ground at the point) and
 * viridis colour (log10 rhoa between the extract's min and max) buffers. A reading hangs from the ground
 * elevation of its own survey line (`surface_z`) at z = surface_z - pseudo_depth, in the frame of the
 * fences and blocks:
 * - terrain-draped: `frame` is verticalFrame({ refElev, groundY }) (dataset-points3d); the collar goes to
 *   groundY(lon, lat, surface_z), so y = surface_z - pseudo_depth - refElev (DEM height where there is no surface_z);
 * - flat scene (no `frame`): y = surface_z - flatBase - pseudo_depth like flatPositions of a model with
 *   topography; `flatBase` is the lowest surface of the models, else the extract's own lowest surface.
 */
export function buildScatterBuffers(ext, origin, frame = null, { flatBase = null } = {}) {
  const n = ext?.count || 0;
  if (!n || !origin) return { positions: new Float32Array(0), colors: new Float32Array(0), count: 0, logMin: 0, logMax: 0 };
  const positions = new Float32Array(n * 3);
  const colors = new Float32Array(n * 3);
  let logMin = Infinity, logMax = -Infinity;
  for (let i = 0; i < n; i++) {
    const v = Math.log10(ext.rho[i]);
    if (v < logMin) logMin = v;
    if (v > logMax) logMax = v;
  }
  const span = logMax > logMin ? logMax - logMin : 1;
  const toY = frame || verticalFrame();
  const base = Number.isFinite(flatBase) ? flatBase : (lowestSurface(ext.surface) ?? 0);
  const table = rampRgb();
  const pt = [0, 0];
  for (let i = 0; i < n; i++) {
    pt[0] = ext.lon[i]; pt[1] = ext.lat[i];
    const [x, z] = lonLatToLocal(pt, origin);
    positions[i * 3] = x;
    const collar = ext.surface ? ext.surface[i] : NaN;
    positions[i * 3 + 1] = frame || !Number.isFinite(collar)
      ? toY(ext.lon[i], ext.lat[i], -ext.depth[i], true, collar)
      : collar - base - ext.depth[i];
    positions[i * 3 + 2] = z;
    const t = (Math.log10(ext.rho[i]) - logMin) / span;
    const c = table[Math.min(RAMP_STEPS - 1, Math.max(0, Math.round(t * (RAMP_STEPS - 1))))];
    colors[i * 3] = c[0]; colors[i * 3 + 1] = c[1]; colors[i * 3 + 2] = c[2];
  }
  return { positions, colors, count: n, logMin, logMax };
}

const sig = (v) => (v >= 100 ? Math.round(v) : Number(v.toPrecision(3)));

/** Hover text of point `i`: "ρa = 296 Ω·m, pseudo-depth 1.2 m". */
export function scatterLabel(ext, i) {
  if (!ext || i < 0 || i >= ext.count) return '';
  return `ρa = ${sig(ext.rho[i])} Ω·m, pseudo-depth ${Number(ext.depth[i].toFixed(1))} m`;
}
